import React, { useState } from 'react';
import PropTypes from 'prop-types';
import '../styles/AddWorkoutModal.css';

/**
 * Modal component for creating or editing workouts
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {function} onSave - Callback to save the workout
 * @param {Object} workout - Workout object to edit (null for create mode)
 * @param {boolean} isEditMode - Whether in edit mode
 * @param {Date} initialDate - Initial date for create mode (optional)
 */
function WorkoutFormModal({ isOpen, onClose, onSave, workout = null, isEditMode = false, initialDate = null, initialTimeOfDay = null }) {
  // Form state is initialized from props on mount. The parent uses a `key` prop to
  // remount this component when switching between create/edit or different workouts,
  // so the useState initializer always runs with the correct props.
  const [formData, setFormData] = useState(() => {
    if (isEditMode && workout) {
      return {
        title: workout.title || '',
        workoutType: workout.workoutType || 'Swim',
        description: workout.workoutDescription || '',
        plannedDate: workout.originallyPlannedDay || '',
        plannedDuration: workout.plannedDuration || '',
        plannedDistanceInMeters: workout.plannedDistanceInMeters || '',
        tss: workout.tss || '',
        timeOfDay: workout.timeOfDay || '',
        workoutLocation: workout.workoutLocation || '',
        heatTraining: workout.heatTraining || false,
      };
    }
    return {
      title: '',
      workoutType: 'Swim',
      description: '',
      plannedDate: initialDate ? initialDate.toISOString().split('T')[0] : '',
      plannedDuration: '',
      plannedDistanceInMeters: '',
      tss: '',
      timeOfDay: initialTimeOfDay || '',
      workoutLocation: '',
      heatTraining: false,
      isRecurring: false,
      recurrenceDaysOfWeek: [],
      recurrenceEndDate: '',
    };
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) {
    return null;
  }

  const workoutTypes = ['Swim', 'Bike', 'Run', 'Strength', 'Other'];
  const timeOfDayOptions = ['morning', 'afternoon', 'evening'];
  const locationOptions = ['indoor', 'outdoor'];
  const daysOfWeek = [
    { label: 'Mon', value: 'MO' },
    { label: 'Tue', value: 'TU' },
    { label: 'Wed', value: 'WE' },
    { label: 'Thu', value: 'TH' },
    { label: 'Fri', value: 'FR' },
    { label: 'Sat', value: 'SA' },
    { label: 'Sun', value: 'SU' },
  ];

  const handleDayToggle = (dayValue) => {
    setFormData(prev => {
      const days = prev.recurrenceDaysOfWeek.includes(dayValue)
        ? prev.recurrenceDaysOfWeek.filter(d => d !== dayValue)
        : [...prev.recurrenceDaysOfWeek, dayValue];
      return { ...prev, recurrenceDaysOfWeek: days };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.plannedDate) {
      newErrors.plannedDate = 'Date is required';
    }

    if (formData.plannedDuration && (isNaN(formData.plannedDuration) || parseFloat(formData.plannedDuration) <= 0)) {
      newErrors.plannedDuration = 'Duration must be a positive number';
    }

    if (formData.plannedDistanceInMeters && (isNaN(formData.plannedDistanceInMeters) || parseFloat(formData.plannedDistanceInMeters) <= 0)) {
      newErrors.plannedDistanceInMeters = 'Distance must be a positive number';
    }

    if (formData.tss && (isNaN(formData.tss) || parseFloat(formData.tss) <= 0)) {
      newErrors.tss = 'TSS must be a positive number';
    }

    if (formData.isRecurring && formData.recurrenceDaysOfWeek.length === 0) {
      newErrors.recurrenceDaysOfWeek = 'Select at least one day to repeat on';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    // Prepare data for API
    const workoutData = {
      title: formData.title.trim(),
      workoutType: formData.workoutType,
      workoutDescription: formData.description.trim(),
      originallyPlannedDay: formData.plannedDate,
      plannedDuration: formData.plannedDuration ? parseFloat(formData.plannedDuration) : null,
      plannedDistanceInMeters: formData.plannedDistanceInMeters ? parseFloat(formData.plannedDistanceInMeters) : null,
      tss: formData.tss ? parseFloat(formData.tss) : null,
      timeOfDay: formData.timeOfDay || null,
      workoutLocation: formData.workoutLocation || null,
      heatTraining: formData.heatTraining,
      isRecurring: formData.isRecurring,
      recurrenceDaysOfWeek: formData.isRecurring ? formData.recurrenceDaysOfWeek : [],
      recurrenceEndDate: formData.isRecurring && formData.recurrenceEndDate ? formData.recurrenceEndDate : null,
    };

    try {
      await onSave(workoutData, isEditMode ? workout?.id : null);
      handleClose();
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to save workout' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      title: '',
      workoutType: 'Swim',
      description: '',
      plannedDate: initialDate ? initialDate.toISOString().split('T')[0] : '',
      plannedDuration: '',
      plannedDistanceInMeters: '',
      tss: '',
      timeOfDay: initialTimeOfDay || '',
      workoutLocation: '',
      heatTraining: false,
      isRecurring: false,
      recurrenceDaysOfWeek: [],
      recurrenceEndDate: '',
    });
    setErrors({});
    onClose();
  };

  const isSubmitting = isLoading;
  const modalTitle = isEditMode ? 'Edit Workout' : 'Add Custom Workout';
  const submitButtonText = isEditMode ? 'Save Workout' : 'Add Workout';

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content add-workout-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose} disabled={isSubmitting}>&times;</button>

        <div className="modal-header">
          <h2 className="modal-title">{modalTitle}</h2>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {errors.submit && <div className="error-message" style={{ marginBottom: '1rem' }}>{errors.submit}</div>}

          <div className="form-group">
            <label htmlFor="title">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Group Ride, Masters Swim"
              className={errors.title ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="workoutType">
                Type <span className="required">*</span>
              </label>
              <select
                id="workoutType"
                name="workoutType"
                value={formData.workoutType}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                {workoutTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="plannedDate">
                Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="plannedDate"
                name="plannedDate"
                value={formData.plannedDate}
                onChange={handleChange}
                className={errors.plannedDate ? 'error' : ''}
                disabled={isEditMode || isSubmitting}
              />
              {errors.plannedDate && <span className="error-message">{errors.plannedDate}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Additional details about the workout"
              rows="3"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="plannedDuration">Duration (hours)</label>
              <input
                type="number"
                id="plannedDuration"
                name="plannedDuration"
                value={formData.plannedDuration}
                onChange={handleChange}
                placeholder="e.g., 1.5"
                step="0.25"
                min="0"
                className={errors.plannedDuration ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.plannedDuration && <span className="error-message">{errors.plannedDuration}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="tss">TSS</label>
              <input
                type="number"
                id="tss"
                name="tss"
                value={formData.tss}
                onChange={handleChange}
                placeholder="e.g., 100"
                min="0"
                className={errors.tss ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.tss && <span className="error-message">{errors.tss}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="plannedDistanceInMeters">Distance (meters)</label>
            <input
              type="number"
              id="plannedDistanceInMeters"
              name="plannedDistanceInMeters"
              value={formData.plannedDistanceInMeters}
              onChange={handleChange}
              placeholder="e.g., 2000 (for swimming)"
              min="0"
              className={errors.plannedDistanceInMeters ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.plannedDistanceInMeters && <span className="error-message">{errors.plannedDistanceInMeters}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="timeOfDay">Time of Day</label>
              <select
                id="timeOfDay"
                name="timeOfDay"
                value={formData.timeOfDay}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="">Not specified</option>
                {timeOfDayOptions.map(time => (
                  <option key={time} value={time}>{time.charAt(0).toUpperCase() + time.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="workoutLocation">Location</label>
              <select
                id="workoutLocation"
                name="workoutLocation"
                value={formData.workoutLocation}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="">Not specified</option>
                {locationOptions.map(location => (
                  <option key={location} value={location}>{location.charAt(0).toUpperCase() + location.slice(1)}</option>
                ))}
              </select>
            </div>

            {(formData.workoutType === 'Bike' || formData.workoutType === 'Run') && (
              <div className="form-group">
                <label htmlFor="heatTraining">Heat training</label>
                <button
                  id="heatTraining"
                  type="button"
                  className={`heat-toggle-button ${formData.heatTraining ? 'on' : 'off'}`}
                  aria-pressed={formData.heatTraining}
                  onClick={() => setFormData(prev => ({ ...prev, heatTraining: !prev.heatTraining }))}
                  disabled={isSubmitting}
                >
                  🔥 Heat training: {formData.heatTraining ? 'On' : 'Off'}
                </button>
              </div>
            )}
          </div>

          {!isEditMode && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isRecurring"
                  checked={formData.isRecurring}
                  onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                  disabled={isSubmitting}
                />
                {' '}Repeat weekly
              </label>

              {formData.isRecurring && (
                <div className="recurrence-options">
                  <fieldset className="form-group">
                    <legend>Repeat on</legend>
                    <div className="day-picker">
                      {daysOfWeek.map(({ label, value }) => (
                        <button
                          key={value}
                          type="button"
                          className={`day-btn${formData.recurrenceDaysOfWeek.includes(value) ? ' day-btn--selected' : ''}`}
                          onClick={() => handleDayToggle(value)}
                          disabled={isSubmitting}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {errors.recurrenceDaysOfWeek && (
                      <span className="error-message">{errors.recurrenceDaysOfWeek}</span>
                    )}
                  </fieldset>

                  <div className="form-group">
                    <label htmlFor="recurrenceEndDate">End date (optional — defaults to 1 year)</label>
                    <input
                      type="date"
                      id="recurrenceEndDate"
                      name="recurrenceEndDate"
                      value={formData.recurrenceEndDate}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

WorkoutFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  workout: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    workoutType: PropTypes.string,
    workoutDescription: PropTypes.string,
    plannedDuration: PropTypes.number,
    plannedDistanceInMeters: PropTypes.number,
    originallyPlannedDay: PropTypes.string,
    tss: PropTypes.number,
    coachComments: PropTypes.string,
    timeOfDay: PropTypes.string,
    workoutLocation: PropTypes.string,
    heatTraining: PropTypes.bool,
    isCustom: PropTypes.bool
  }),
  isEditMode: PropTypes.bool,
  initialDate: PropTypes.instanceOf(Date),
  initialTimeOfDay: PropTypes.string
};

WorkoutFormModal.defaultProps = {
  workout: null,
  isEditMode: false,
  initialDate: null,
  initialTimeOfDay: null
};

export default WorkoutFormModal;
